import { motion } from "framer-motion";
import { Page } from "@/components/Page";
import { PageShell } from "@/components/PageShell";
import { EASE, Eyebrow, FadeUp } from "@/components/Reveal";
import { LuxButton } from "@/components/LuxButton";
import { useUI } from "@/context/UIContext";
import { IMG } from "@/lib/images";

const PRIVILEGES = [
  ["01", "Dedicated concierge", "A named senior director, available around the clock, in every timezone."],
  ["02", "Priority access", "First call on reservations, tickets and experiences before release."],
  ["03", "Travel assistance", "Aircraft, transfers and itineraries arranged door to door."],
  ["04", "Exclusive experiences", "Member-only occasions across the season's great cities."],
  ["05", "Private reservations", "Tables and venues held under arrangements unavailable publicly."],
  ["06", "Personalised service", "Your preferences remembered, anticipated and never repeated back to you."],
];

export default function Membership() {
  const { openEnquiry } = useUI();
  return (
    <Page testId="membership-page">
      <PageShell
        testId="membership-hero"
        eyebrow="Membership"
        title={
          <>
            Membership without <span className="gold-text italic">compromise.</span>
          </>
        }
        intro="MDS Arabama is deliberately limited. Membership is extended by application and personal referral, and reviewed annually."
        image={IMG.resort}
        imageAlt="Infinity pool of a private resort at dusk"
      >
        <section className="mx-auto max-w-[1280px] px-6 py-24 md:py-32 lg:px-12">
          <FadeUp>
            <Eyebrow>The Privileges</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 max-w-xl font-serif text-3xl font-medium leading-tight text-ivory sm:text-4xl">
              What membership confers
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-x-14 sm:grid-cols-2">
            {PRIVILEGES.map(([num, title, text], i) => (
              <FadeUp key={num} delay={(i % 2) * 0.1}>
                <div className="group border-t border-[rgba(200,164,93,0.16)] py-9">
                  <span className="gold-text font-serif text-2xl font-light italic">{num}</span>
                  <h3 className="mt-3 font-serif text-[24px] font-medium text-ivory transition-colors duration-500 group-hover:text-champagne-light">
                    {title}
                  </h3>
                  <p className="mt-3 max-w-md text-[13px] font-light leading-relaxed text-ivory-muted">
                    {text}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        <section className="grid lg:grid-cols-2">
          <div className="relative min-h-[380px] overflow-hidden lg:min-h-[560px]" data-cursor>
            <motion.img
              src={IMG.wineCellar}
              alt="Private wine cellar with racks of rare vintages"
              loading="lazy"
              initial={{ scale: 1.06, opacity: 0.6 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.4, ease: EASE }}
              className="img-lux-grade absolute inset-0 h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col justify-center bg-[#080808] px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
            <FadeUp>
              <Eyebrow>The Application</Eyebrow>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 max-w-md font-serif text-[32px] font-medium leading-[1.12] text-ivory sm:text-4xl">
                Selective, <span className="gold-text italic">by design.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-ivory-muted">
                We accept a small number of new members each year. Every
                application is reviewed personally by our membership committee,
                and successful candidates are invited to a private introduction.
              </p>
            </FadeUp>
            <FadeUp delay={0.3} className="mt-10">
              <LuxButton onClick={() => openEnquiry("Membership")} arrow testId="membership-apply-button">
                Apply for Membership
              </LuxButton>
            </FadeUp>
          </div>
        </section>
      </PageShell>
    </Page>
  );
}
